export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo.png" alt="Post Your Content" className="w-10 h-10" />
          <span className="text-[#3C4048] text-lg font-bold" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
            Post Your Content
          </span>
        </div>

        <h1 className="text-3xl font-bold text-[#3C4048] mb-2" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
          Privacy Policy
        </h1>
        <p className="text-[#7A9090] text-sm mb-10">Last updated: 17 August 2026</p>

        <div className="space-y-8 text-[#3C4048] text-sm leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-0 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_a]:text-[#3A7A8C] [&_a]:font-semibold [&_a]:underline" style={{ fontFamily: "Montserrat" }}>
          <section>
            <h2>1. Who we are</h2>
            <p>
              "Post Your Content" ("we", "us", "our") is a multi-client social media management platform that
              helps businesses and agencies plan, approve, and publish social media content. This policy explains
              what personal data we collect, why, and the rights you have under UK GDPR and EU GDPR.
            </p>
            <p>Contact us about privacy matters at <a href="mailto:hello@postyourcontent.com">hello@postyourcontent.com</a>.</p>
          </section>

          <section>
            <h2>2. What data we collect</h2>
            <ul>
              <li><strong>Account data</strong> — name, email address and authentication details, managed via our identity provider (Clerk).</li>
              <li><strong>Workspace &amp; brand data</strong> — your company profile, brand guidelines, palette, fonts and logos you add to the platform.</li>
              <li><strong>Content you create</strong> — post captions, images, voiceovers and other media you generate, upload or approve within the platform.</li>
              <li><strong>Connected social accounts</strong> — when you connect Facebook, Instagram, LinkedIn, TikTok or YouTube, we store an encrypted access token and basic account/page identifiers (e.g. Page name, channel name) so we can publish on your behalf. We never see or store your social media password.</li>
              <li><strong>Usage data</strong> — log data such as IP address, browser type and pages visited, used for security and troubleshooting.</li>
            </ul>
          </section>

          <section>
            <h2>3. How we use your data</h2>
            <ul>
              <li>To provide the core service — content planning, AI-assisted drafting, approvals, and publishing to the social accounts you connect.</li>
              <li>To operate an approval workflow ("Silence is Approval") that requires human review of AI-generated content before it is ever published.</li>
              <li>To improve and secure the platform, and to respond to support requests.</li>
            </ul>
          </section>

          <section>
            <h2>4. Third-party services we use</h2>
            <p>We rely on the following processors to operate the platform. Each only receives the minimum data needed to perform its function:</p>
            <ul>
              <li><strong>Clerk</strong> — authentication and account management.</li>
              <li><strong>Anthropic (Claude) &amp; OpenAI</strong> — AI text, image and document/audio understanding for content drafts.</li>
              <li><strong>Inworld AI</strong> — AI voiceover generation.</li>
              <li><strong>Storyblocks, Pexels &amp; Unsplash</strong> — stock photo search.</li>
              <li><strong>Meta (Facebook/Instagram), LinkedIn, TikTok, Google (YouTube)</strong> — direct publishing to the social accounts you connect.</li>
              <li><strong>MongoDB</strong> — secure database hosting for your workspace data.</li>
            </ul>
          </section>

          <section>
            <h2>5. Legal basis for processing</h2>
            <p>
              We process your data under UK/EU GDPR on the basis of: performance of a contract (providing the
              service you signed up for), our legitimate interests (security, service improvement), and your
              consent where you explicitly connect a third-party social account.
            </p>
          </section>

          <section>
            <h2>6. Data retention</h2>
            <p>
              We retain your workspace data for as long as your account is active. If you disconnect a social
              account, its access tokens are deleted immediately. You can request deletion of your account and
              associated data at any time via the contact details below.
            </p>
          </section>

          <section>
            <h2>7. International data transfers</h2>
            <p>
              Some of our processors (listed above) operate outside the UK/EEA. Where this happens, we rely on
              appropriate safeguards such as Standard Contractual Clauses to ensure your data remains protected.
            </p>
          </section>

          <section>
            <h2>8. Your rights</h2>
            <p>Under UK/EU GDPR, you have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate data</li>
              <li>Request erasure of your data</li>
              <li>Restrict or object to certain processing</li>
              <li>Receive your data in a portable format</li>
              <li>Withdraw consent at any time (e.g. by disconnecting a social account)</li>
              <li>Lodge a complaint with your local data protection authority (in the UK, the ICO — ico.org.uk)</li>
            </ul>
          </section>

          <section>
            <h2>9. Cookies</h2>
            <p>
              We use essential cookies required for authentication and session management. We do not use
              third-party advertising or tracking cookies.
            </p>
          </section>

          <section>
            <h2>10. Children's privacy</h2>
            <p>Post Your Content is a business tool and is not directed at, or intended for use by, children under 16.</p>
          </section>

          <section>
            <h2>11. Security</h2>
            <p>
              Social account access tokens are stored encrypted at rest. Access to workspace data is scoped per
              organization so that one client's data is never visible to another.
            </p>
          </section>

          <section>
            <h2>12. Changes to this policy</h2>
            <p>We may update this policy from time to time. Material changes will be reflected by updating the "Last updated" date above.</p>
          </section>

          <section>
            <h2>13. Contact us</h2>
            <p>
              For any privacy questions or to exercise your rights, email <a href="mailto:hello@postyourcontent.com">hello@postyourcontent.com</a>.
            </p>
          </section>
        </div>

        <a href="/" className="inline-block mt-10 text-[#3A7A8C] text-sm font-semibold hover:underline" data-testid="privacy-back-link">
          ← Back to Post Your Content
        </a>
      </div>
    </div>
  );
}
